import { Component, OnInit, ChangeDetectorRef, OnDestroy, HostListener, ElementRef } from '@angular/core';
import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../core/reducers';
import { currentUser } from '../../../core/auth';
import { User } from '../../../core/auth/_models/user.model';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { ModalCreateSignInUpComponent } from '@app/views/pages/sign-in-up/modal-create-sign-in-up/modal-create-sign-in-up.component';

export interface AttachmentFile {
  id: string;
  name: string;
  file_name: string | null;
  file_size: string | null;
  physical_path: string | null;
  created_at: string;
  created_by: string;
  modified_at: string | null;
  modified_by: string | null;
  image_path: string | null;
  description: string | null;
  code: string;
  sub_code: string;
  is_paid: boolean;
  price: number;
  currency: string;
  download_count: number;
  limit_download: number | null;
  preview_path: string | null;
  is_active: boolean;
  is_deleted: boolean;
}

export interface AttachmentCategory {
  id: string;
  name: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
  attachments: AttachmentFile[];
}

interface CategoryApiResponse {
  success: boolean;
  message?: string;
  data: {
    collection: AttachmentCategory[];
    total: number;
    pagesize: number;
    pageIndex: number;
  };
}

interface MomoPaymentResponse {
  success: boolean;
  message?: string;
  message_en?: string;
  payUrl?: string;
  purchaseId?: string;
  data?: MomoPaymentData;
}

interface MomoPaymentData {
  payUrl?: string;
  purchaseId?: string;
}

interface MomoIpnPayload {
  partnerCode: string;
  orderId: string;
  requestId: string;
  amount: number;
  orderInfo: string;
  orderType: string;
  transId: number;
  resultCode: number;
  message: string;
  payType: string;
  responseTime: number;
  extraData: string;
  signature: string;
}

interface MomoIpnResponse {
  success: boolean;
  message?: string;
  data?: MomoIpnData;
}

interface MomoIpnData {
  attachmentId: string;
  [key: string]: string | number | boolean;
}

interface PurchasedDownloadsResponse {
  success: boolean;
  data?: {
    collection?: PurchasedDownloadItem[];
  };
}

interface PurchasedDownloadItem {
  attachmentId: string;
  [key: string]: string | number | boolean;
}

@Component({
  selector: 'app-download',
  templateUrl: './download.component.html',
  styleUrls: ['./download.component.scss']
})
export class DownloadComponent implements OnInit, OnDestroy {
  categories: AttachmentCategory[] = [];
  isLoading = false;
  hasError = false;
  errorMessage = '';
  page = 1;
  pageSize = 12;
  total = 0;
  protected basePath = environment.backendhost;

  showPreviewModal = false;
  selectedFile: AttachmentFile | null = null;
  previewUrl = '';
  isPreviewable = false;
  previewError = false;
  isLoadingPreview = false;

  /** true while creating Momo payment link */
  isProcessingPayment = false;
  /** true after payment redirected – waiting for IPN callback */
  isPendingPayment = false;
  /** purchaseId from create-payment response, used to confirm with IPN */
  currentPurchaseId = '';
  /** true while processing Momo IPN after redirect */
  isProcessingIpn = false;
  /** true when IPN processed successfully – show download button */
  ipnSuccess = false;
  /** true when backend reports the user still has free download(s) remaining */
  hasRemainingDownloads = false;
  /** attachmentId extracted from IPN extraData after successful payment */
  paidAttachmentId = '';
  /** true while confirming payment via IPN after user clicks "Tôi đã thanh toán" */
  isConfirmingPayment = false;
  isAdmin = false;
  /** Set of attachmentIds the current user has already purchased */
  purchasedAttachmentIds = new Set<string>();
  /** Current logged-in user id, used for purchased-downloads API */
  private currentUserId: number | null = null;
  private userSub: Subscription;
  private searchSub: Subscription;
  /** Saved scroll position before opening modal */
  private savedScrollY = 0;

  /** Search */
  searchText = '';
  searchResults: AttachmentFile[] = [];
  searchTotal = 0;
  isSearching = false;
  showSearchResults = false;
  searchPage = 1;
  searchPageSize = 12;
  searchPageSizeOptions = [12, 24, 36, 48];
  searchKeyUp = new Subject<string>();
  selectedCategoryId = '';
  isCategoryDropdownOpen = false;

  constructor(
    protected httpClient: HttpClient,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private elementRef: ElementRef
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-category-dropdown')) {
      this.isCategoryDropdownOpen = false;
    }
  }

  ngOnInit(): void {
    this.userSub = this.store.pipe(select(currentUser)).subscribe((user: User | undefined) => {
      this.isAdmin = this.checkIsAdmin(user);
      if (user && user.id) {
        if (user.id !== this.currentUserId) {
          this.currentUserId = user.id as number;
          this.loadPurchasedDownloads();
        }
      } else {
        this.currentUserId = null;
        this.purchasedAttachmentIds.clear();
      }
      this.cdr.detectChanges();
    });
    this.loadCategories();

    // Setup search debounce
    this.searchSub = this.searchKeyUp
      .pipe(
        map((value: string) => value.trim()),
        debounceTime(500)
      )
      .subscribe((value: string) => {
        this.searchText = value;
        this.searchPage = 1;
        if (value || this.selectedCategoryId) {
          this.searchAttachments();
        } else {
          this.showSearchResults = false;
          this.searchResults = [];
          this.cdr.detectChanges();
        }
      });

    // Detect Momo redirect: URL contains partnerCode=MOMO after payment
    const params = this.route.snapshot.queryParams;
    if (params['partnerCode'] === 'MOMO') {
      this.handleMomoCallback(params);
    }
  }

  ngOnDestroy(): void {
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
    // Ensure body scroll is restored if modal was open
    document.body.classList.remove('modal-open-no-scroll');
    document.body.style.top = '';
  }

  searchAttachments(): void {
    if (!this.searchText && !this.selectedCategoryId) {
      this.showSearchResults = false;
      this.searchResults = [];
      return;
    }
    this.isSearching = true;
    this.showSearchResults = true;
    const apiUrl = `${this.basePath}/api/attachments`;
    const params: any = {
      Page: this.searchPage.toString(),
      PageSize: this.searchPageSize.toString()
    };
    if (this.searchText) {
      params.name = this.searchText;
    }
    if (this.selectedCategoryId) {
      params.Filters = `attachment_category_id==${this.selectedCategoryId}`;
    }
    this.httpClient
      .get<CategoryApiResponse>(apiUrl, { params })
      .subscribe({
        next: (response: CategoryApiResponse) => {
          if (response && response.success) {
            this.searchResults = (response.data.collection as unknown as AttachmentFile[]) || [];
            this.searchTotal = response.data.total || 0;
          } else {
            this.searchResults = [];
            this.searchTotal = 0;
          }
          this.isSearching = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.searchResults = [];
          this.searchTotal = 0;
          this.isSearching = false;
          this.cdr.detectChanges();
        }
      });
  }

  onSearchPageChange(page: number): void {
    this.searchPage = page;
    this.searchAttachments();
    window.scrollTo(0, 0);
  }

  onSearchPageSizeChange(size: number): void {
    this.searchPageSize = size;
    this.searchPage = 1;
    this.searchAttachments();
  }

  clearSearch(): void {
    this.searchText = '';
    this.selectedCategoryId = '';
    this.showSearchResults = false;
    this.searchResults = [];
    this.searchPage = 1;
    this.cdr.detectChanges();
  }

  onSearchCategoryChange(categoryId: string): void {
    this.selectedCategoryId = categoryId;
    this.searchPage = 1;
    if (this.searchText || this.selectedCategoryId) {
      this.searchAttachments();
    } else {
      this.showSearchResults = false;
      this.searchResults = [];
      this.cdr.detectChanges();
    }
  }

  toggleCategoryDropdown(): void {
    this.isCategoryDropdownOpen = !this.isCategoryDropdownOpen;
  }

  closeCategoryDropdown(): void {
    this.isCategoryDropdownOpen = false;
  }

  selectCategory(categoryId: string): void {
    this.isCategoryDropdownOpen = false;
    this.onSearchCategoryChange(categoryId);
  }

  getSelectedCategoryName(): string {
    if (!this.selectedCategoryId) {
      return 'Tất cả danh mục';
    }
    const found = this.categories.find(c => c.id === this.selectedCategoryId);
    return found ? found.name : 'Tất cả danh mục';
  }

  private checkIsAdmin(user: User | undefined | null): boolean {
    if (!user || !user.roles || !Array.isArray(user.roles)) {
      return false;
    }
    const adminRoles = ['Admin', 'Content Admin', 'Salesman'];
    return (user.roles as unknown as string[]).some((role: string) => adminRoles.includes(role));
  }

  canAccessPaidFile(file: AttachmentFile): boolean {
    if (!file.is_paid) return true;
    return this.isAdmin;
  }

  loadCategories(): void {
    this.isLoading = true;
    this.hasError = false;
    const apiUrl = `${this.basePath}/api/attachment_categories`;
    this.httpClient
      .get<CategoryApiResponse>(apiUrl, {
        params: {
          filters: '',
          sorts: '',
          page: this.page.toString(),
          pageSize: this.pageSize.toString()
        }
      })
      .subscribe({
        next: (response: CategoryApiResponse) => {
          if (response && response.success) {
            this.categories = response.data.collection || [];
            this.total = response.data.total || 0;
          } else {
            this.categories = [];
            this.total = 0;
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.hasError = true;
          this.errorMessage =
            'Đã xảy ra lỗi khi tải dữ liệu. Vui lòng kiểm tra kết nối mạng và thử lại.';
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  /** Fetch all attachment IDs the current user has already purchased */
  private loadPurchasedDownloads(): void {
    if (!this.currentUserId) return;

    const userToken = localStorage.getItem(environment.authTokenKey);
    const headers = new HttpHeaders({ Authorization: `Bearer ${userToken}` });
    const apiUrl = `${this.basePath}/api/attachments/statistics/purchased-downloads`;

    this.httpClient
      .get<PurchasedDownloadsResponse>(apiUrl, {
        headers,
        params: { userId: String(this.currentUserId), pageSize: '1000' }
      })
      .subscribe({
        next: (response: PurchasedDownloadsResponse) => {
          if (response && response.success && response.data && response.data.collection) {
            this.purchasedAttachmentIds = new Set<string>(
              response.data.collection.map((item: PurchasedDownloadItem) => item.attachmentId)
            );
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('Could not load purchased downloads', err);
        }
      });
  }
  checkLoginOrShowModal(): boolean {
    const token = localStorage.getItem(environment.authTokenKey);
    const hasToken = !!(token && token.length > 10);
    const actuallyLoggedIn = !!this.currentUserId || hasToken;

    if (!actuallyLoggedIn) {
      if (this.showPreviewModal) {
        this.closePreview();
      }

      const dialogRef = this.dialog.open(ModalCreateSignInUpComponent, {
        width: '900px',
        data: {
          type: 'signin',
          returnUrl: location.pathname
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        // Reload after login if necessary
      });
      return false;
    }
    return true;
  }

  downloadFile(attachment: AttachmentFile): void {
    if (!this.checkLoginOrShowModal()) {
      return;
    }

    if (!this.canAccessPaidFile(attachment)) {
      return;
    }

    const downloadApiUrl = `${this.basePath}/api/attachments/download`;
    const userToken = localStorage.getItem(environment.authTokenKey);

    if (attachment.is_paid) {
      // Admin downloads paid file by id
      const fullUrl = `${downloadApiUrl}?id=${encodeURIComponent(attachment.id)}`;
      this.triggerDownloadLink(fullUrl, attachment.file_name || 'download', userToken);
    } else if (attachment.physical_path) {
      // Free file downloads by url
      let downloadHash = attachment.physical_path;
      if (downloadHash.includes('url=')) {
        downloadHash = downloadHash.split('url=')[1];
      } else {
        downloadHash = encodeURIComponent(downloadHash);
      }

      const fullUrl = `${downloadApiUrl}?url=${downloadHash}`;
      this.triggerDownloadLink(fullUrl, attachment.file_name || 'download', userToken);
    }
  }

  private triggerDownloadLink(href: string, fileName: string, token: string | null): void {
    // Use HttpClient to download with authorization header
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    this.httpClient
      .get(href, {
        responseType: 'blob',
        headers: headers
      })
      .subscribe({
        next: (blob: Blob) => {
          if (blob.size < 10000) {
            this.readBlobAsText(blob)
              .then((text) => {
                try {
                  const json = JSON.parse(text);
                  if (json.statusCode || json.success === false) {
                    alert(
                      'Lỗi tải file: ' + (json.message || json.message_en || 'Không tải được file.')
                    );
                    return;
                  }
                } catch (e) {
                  // Not JSON, proceed
                }
                this.forceDownload(blob, fileName);
              })
              .catch(() => {
                this.forceDownload(blob, fileName);
              });
          } else {
            this.forceDownload(blob, fileName);
          }
        },
        error: (err) => {
          console.error('Download error:', err);
          alert('Có lỗi xảy ra khi tải file.');
        }
      });
  }

  private forceDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // ─── Preview ─────────────────────────────────────────────────────────────────

  openPreview(attachment: AttachmentFile): void {
    this.selectedFile = attachment;
    this.previewError = false;
    this.isLoadingPreview = false;
    this.isProcessingPayment = false;
    this.isPendingPayment = false;
    this.isConfirmingPayment = false;
    this.currentPurchaseId = '';
    this.showPreviewModal = true;
    this.savedScrollY = window.scrollY;
    document.body.classList.add('modal-open-no-scroll');
    document.body.style.top = `-${this.savedScrollY}px`;

    // Check if user already purchased this file → unlock immediately
    this.hasRemainingDownloads = this.purchasedAttachmentIds.has(attachment.id);

    // Paid file – non-admin: load 10-page preview via /api/attachments/{id}/preview
    if (attachment.is_paid && !this.isAdmin) {
      this.isLoadingPreview = true;
      const previewApiUrl = `${this.basePath}/api/attachments/${encodeURIComponent(attachment.id)}/preview`;

      // Get token from localStorage and add to headers
      const userToken = localStorage.getItem(environment.authTokenKey);
      const headers = new HttpHeaders({
        Authorization: `Bearer ${userToken}`
      });

      this.httpClient
        .get(previewApiUrl, {
          responseType: 'blob',
          headers: headers,
          observe: 'response'
        })
        .subscribe({
          next: (response: HttpResponse<Blob>) => {
            if (response.body) {
              const blob = response.body;

              // Backend may return 200 with JSON error body (e.g. 403 "requires purchase")
              // For small responses (<10KB), read as text to detect JSON errors
              // Real PDF/image previews are always much larger
              if (blob.size < 10000) {
                this.readBlobAsText(blob)
                  .then((text: string) => {
                    try {
                      const json = JSON.parse(text);
                      if (json.statusCode || json.success === false) {
                        console.warn(
                          'Preview API returned JSON error:',
                          json.message || json.message_en
                        );
                        this.isPreviewable = false;
                        this.previewUrl = '';
                        this.isLoadingPreview = false;
                        this.cdr.detectChanges();
                        return;
                      }
                    } catch (e) {
                      // Not JSON — it's a legitimate small file, proceed normally
                    }
                    this.applyPreviewBlob(blob, attachment);
                  })
                  .catch(() => {
                    this.isPreviewable = false;
                    this.previewUrl = '';
                    this.isLoadingPreview = false;
                    this.cdr.detectChanges();
                  });
                return;
              }

              // Large blob — definitely a real file preview
              this.applyPreviewBlob(blob, attachment);
            } else {
              this.isPreviewable = false;
              this.previewUrl = '';
              this.isLoadingPreview = false;
              this.cdr.detectChanges();
            }
          },
          error: (err) => {
            console.error('Preview error:', err);
            this.isPreviewable = false;
            this.previewUrl = '';
            this.isLoadingPreview = false;
            this.cdr.detectChanges();
          }
        });
      this.cdr.detectChanges();
      return;
    }

    // Free file or admin: fetch via HttpClient with auth token
    if (attachment.physical_path) {
      this.isLoadingPreview = true;
      const fileUrl = attachment.physical_path.startsWith('http')
        ? attachment.physical_path
        : `${this.basePath}${attachment.physical_path}`;

      const extension = this.getFileExtension(attachment.file_name || '');
      if (!this.canPreview(extension)) {
        this.isPreviewable = false;
        this.previewUrl = '';
        this.isLoadingPreview = false;
        this.cdr.detectChanges();
        return;
      }

      const userToken = localStorage.getItem(environment.authTokenKey);
      const freeHeaders = new HttpHeaders({
        Authorization: `Bearer ${userToken}`
      });

      this.httpClient
        .get(fileUrl, {
          responseType: 'blob',
          headers: freeHeaders,
          observe: 'response'
        })
        .subscribe({
          next: (resp: HttpResponse<Blob>) => {
            if (resp.body) {
              const freeBlob = resp.body;

              // Check for JSON error responses (small blobs)
              if (freeBlob.size < 10000) {
                this.readBlobAsText(freeBlob)
                  .then((text: string) => {
                    try {
                      const json = JSON.parse(text);
                      if (json.statusCode || json.success === false) {
                        console.warn(
                          'Free preview API returned JSON error:',
                          json.message || json.message_en
                        );
                        this.isPreviewable = false;
                        this.previewUrl = '';
                        this.isLoadingPreview = false;
                        this.cdr.detectChanges();
                        return;
                      }
                    } catch (e) {
                      // Not JSON — legitimate small file
                    }
                    this.applyPreviewBlob(freeBlob, attachment);
                  })
                  .catch(() => {
                    this.isPreviewable = false;
                    this.previewUrl = '';
                    this.isLoadingPreview = false;
                    this.cdr.detectChanges();
                  });
                return;
              }

              this.applyPreviewBlob(freeBlob, attachment);
            } else {
              this.isPreviewable = false;
              this.previewUrl = '';
              this.isLoadingPreview = false;
              this.cdr.detectChanges();
            }
          },
          error: (err) => {
            console.error('Free preview error:', err);
            this.isPreviewable = false;
            this.previewUrl = '';
            this.isLoadingPreview = false;
            this.cdr.detectChanges();
          }
        });
    }

    this.cdr.detectChanges();
  }

  private buildViewerUrl(fileUrl: string, fileName: string): string {
    const extension = this.getFileExtension(fileName);
    if (extension === 'xls' || extension === 'xlsx') {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
    }
    if (['pdf', 'doc', 'docx', 'ppt', 'pptx'].includes(extension)) {
      return `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    }
    return fileUrl;
  }

  /** Create blob URL and apply preview state for a valid file blob */
  private applyPreviewBlob(blob: Blob, attachment: AttachmentFile): void {
    const blobUrl = URL.createObjectURL(blob);
    const extension = this.getFileExtension(attachment.file_name || '');

    this.isPreviewable = this.canPreview(extension);
    if (this.isPreviewable) {
      if (extension === 'pdf') {
        this.previewUrl = blobUrl;
      } else if (this.isImageFile(attachment.file_name || '')) {
        this.previewUrl = blobUrl;
      } else {
        this.previewUrl = this.buildViewerUrl(blobUrl, attachment.file_name || '');
      }
    } else {
      this.previewUrl = '';
    }
    this.isLoadingPreview = false;
    this.cdr.detectChanges();
  }

  closePreview(): void {
    this.showPreviewModal = false;
    document.body.classList.remove('modal-open-no-scroll');
    document.body.style.top = '';
    window.scrollTo(0, this.savedScrollY);
    this.selectedFile = null;
    this.previewUrl = '';
    this.isPreviewable = false;
    this.previewError = false;
    this.isLoadingPreview = false;
    this.isProcessingPayment = false;
    this.isPendingPayment = false;
    this.hasRemainingDownloads = false;
  }

  private handleMomoCallback(params: { [key: string]: string }): void {
    // Only process if resultCode = 0 (success)
    const resultCode = parseInt(params['resultCode'] || '1', 10);

    // Clean URL: remove Momo query params so user doesn't re-trigger on refresh
    this.router.navigate([], {
      queryParams: {},
      replaceUrl: true
    });

    if (resultCode !== 0) {
      // Payment failed / cancelled – show a toast or alert
      alert('Thanh toán không thành công. Mã lỗi: ' + resultCode);
      return;
    }

    this.isProcessingIpn = true;
    this.cdr.detectChanges();

    const payload: MomoIpnPayload = {
      partnerCode: params['partnerCode'] || '',
      orderId: params['orderId'] || '',
      requestId: params['requestId'] || '',
      amount: parseInt(params['amount'] || '0', 10),
      orderInfo: decodeURIComponent(params['orderInfo'] || ''),
      orderType: params['orderType'] || '',
      transId: parseInt(params['transId'] || '0', 10),
      resultCode: resultCode,
      message: decodeURIComponent(params['message'] || ''),
      payType: params['payType'] || '',
      responseTime: parseInt(params['responseTime'] || '0', 10),
      extraData: params['extraData'] || '',
      signature: params['signature'] || ''
    };

    const ipnUrl = `${this.basePath}/api/momo/document/ipn`;
    const userToken = localStorage.getItem(environment.authTokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${userToken}`
    });

    this.httpClient.post<MomoIpnResponse>(ipnUrl, payload, { headers: headers }).subscribe({
      next: (response: MomoIpnResponse) => {
        this.isProcessingIpn = false;

        if (response && response.success) {
          this.ipnSuccess = true;
          // Extract attachmentId from extraData (base64 JSON) or from IPN response
          const attachmentId = this.extractAttachmentId(payload.extraData, response);
          if (attachmentId) {
            this.paidAttachmentId = attachmentId;
            // Auto-trigger download
            this.downloadPaidById(attachmentId);
          }
        } else {
          alert('Xác nhận thanh toán thất bại. Vui lòng liên hệ hỗ trợ.');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isProcessingIpn = false;
        alert('Lỗi khi xác nhận thanh toán. Vui lòng liên hệ hỗ trợ.');
        this.cdr.detectChanges();
      }
    });
  }

  private extractAttachmentId(extraData: string, response: MomoIpnResponse): string {
    // Try IPN response first
    if (response && response.data && response.data.attachmentId) {
      return response.data.attachmentId;
    }
    // Decode extraData base64 JSON
    if (extraData) {
      try {
        const decoded = atob(extraData);
        const parsed = JSON.parse(decoded);
        if (parsed && parsed.attachmentId) {
          return parsed.attachmentId;
        }
      } catch (e) {
        // ignore decode errors
      }
    }
    return '';
  }

  /** Download a paid attachment by id (after successful IPN confirmation) */
  downloadPaidById(attachmentId: string): void {
    const downloadApiUrl = `${this.basePath}/api/attachments/download`;
    const fullUrl = `${downloadApiUrl}?id=${encodeURIComponent(attachmentId)}`;
    const userToken = localStorage.getItem(environment.authTokenKey);
    this.triggerDownloadLink(fullUrl, 'document', userToken);
  }

  createMomoPayment(attachment: AttachmentFile): void {
    if (!this.checkLoginOrShowModal()) return;
    if (this.isProcessingPayment) return;
    this.isProcessingPayment = true;
    this.cdr.detectChanges();

    const lang = localStorage.getItem('language') || 'vi';
    const payUrl = `${this.basePath}/api/momo/document/create-payment`;
    const userToken = localStorage.getItem(environment.authTokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${userToken}`
    });

    this.httpClient
      .post<MomoPaymentResponse>(payUrl, null, {
        params: {
          attachmentId: attachment.id,
          lang
        },
        headers: headers
      })
      .subscribe({
        next: (response: MomoPaymentResponse) => {
          this.isProcessingPayment = false;
          const momoPayUrl =
            response && response.payUrl
              ? response.payUrl
              : response && response.data && response.data.payUrl
                ? response.data.payUrl
                : '';
          const purchaseId =
            response && response.purchaseId
              ? response.purchaseId
              : response && response.data && response.data.purchaseId
                ? response.data.purchaseId
                : '';

          const message =
            response && response.message
              ? response.message
              : response && response.message_en
                ? response.message_en
                : '';
          const lowerMsg = (message || '').toLowerCase();

          const alreadyHaveKeywords = ['still have', 'vẫn còn', 'còn', 'lượt tải'];
          const hasAlready = alreadyHaveKeywords.some((k) => lowerMsg.includes(k));

          if (response && response.success && momoPayUrl) {
            this.isPendingPayment = true;
            this.currentPurchaseId = purchaseId;
            window.open(momoPayUrl, '_blank');
          } else if (response && !response.success && hasAlready) {
            // User already has remaining downloads – show download button directly, no payment needed
            this.hasRemainingDownloads = true;
            this.paidAttachmentId = attachment.id;
            // Sync into local purchased set so next openPreview() also unlocks
            this.purchasedAttachmentIds.add(attachment.id);
          } else {
            alert('Không thể tạo liên kết thanh toán. ' + (message || 'Vui lòng thử lại.'));
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.isProcessingPayment = false;
          alert('Đã xảy ra lỗi khi tạo thanh toán. Vui lòng thử lại.');
          this.cdr.detectChanges();
        }
      });
  }

  confirmPaymentAndDownload(attachment: AttachmentFile): void {
    if (this.isConfirmingPayment) return;
    this.isConfirmingPayment = true;
    this.cdr.detectChanges();

    const ipnUrl = `${this.basePath}/api/momo/document/ipn`;
    const userToken = localStorage.getItem(environment.authTokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${userToken}`
    });

    this.httpClient
      .post<MomoIpnResponse>(ipnUrl, { purchaseId: this.currentPurchaseId }, { headers: headers })
      .subscribe({
        next: (response: MomoIpnResponse) => {
          this.isConfirmingPayment = false;
          if (response && response.success) {
            this.isPendingPayment = false;
            this.ipnSuccess = true;
            this.paidAttachmentId = attachment.id;
            this.purchasedAttachmentIds.add(attachment.id);
            this.closePreview();
            this.downloadPaidById(attachment.id);
          } else {
            const msg =
              response && response.message ? response.message : 'Thanh toán chưa được xác nhận.';
            alert(msg + '\nVui lòng đợi vài giây rồi thử lại.');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.isConfirmingPayment = false;
          alert('Không thể xác nhận thanh toán. Vui lòng thử lại sau.');
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Fallback: download directly by attachment id (dùng sau khi Momo redirect tự động).
   */
  downloadAfterPayment(attachment: AttachmentFile): void {
    const downloadApiUrl = `${this.basePath}/api/attachments/download`;
    const fullUrl = `${downloadApiUrl}?id=${encodeURIComponent(attachment.id)}`;
    const userToken = localStorage.getItem(environment.authTokenKey);
    this.triggerDownloadLink(fullUrl, attachment.file_name || 'download', userToken);
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  canPreview(extension: string): boolean {
    const previewableTypes = [
      'pdf',
      'jpg',
      'jpeg',
      'png',
      'gif',
      'bmp',
      'webp',
      'svg',
      'doc',
      'docx',
      'ppt',
      'pptx',
      'txt'
    ];
    return previewableTypes.includes(extension.toLowerCase());
  }

  getFileExtension(fileName: string): string {
    if (!fileName) return '';
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  isDocumentFile(fileName: string): boolean {
    const extension = this.getFileExtension(fileName);
    return ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(extension);
  }

  isImageFile(fileName: string): boolean {
    const extension = this.getFileExtension(fileName);
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'fas fa-file';

    const extension = this.getFileExtension(fileName);

    switch (extension) {
      case 'pdf':
        return 'fas fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fas fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fas fa-file-excel';
      case 'ppt':
      case 'pptx':
        return 'fas fa-file-powerpoint';
      case 'zip':
      case 'rar':
        return 'fas fa-file-archive';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'fas fa-file-image';
      case 'txt':
        return 'fas fa-file-alt';
      default:
        return 'fas fa-file';
    }
  }

  getCategoryIcon(categoryName: string): string {
    const name = categoryName.toLowerCase();
    if (name.includes('brochure') || name.includes('catalog')) {
      return 'fas fa-book-open';
    } else if (name.includes('video')) {
      return 'fas fa-video';
    } else if (name.includes('manual') || name.includes('guide')) {
      return 'fas fa-tools';
    } else if (name.includes('spec') || name.includes('technical')) {
      return 'fas fa-cogs';
    }
    return 'fas fa-folder';
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadCategories();
  }

  formatPrice(price: number, currency: string): string {
    if (!price) return '0 ' + (currency || 'VND');
    return price.toLocaleString('vi-VN') + ' ' + (currency || 'VND');
  }

  truncateText(text: string, maxLength: number = 25): string {
    if (!text || text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  }

  /** Read a Blob as text using FileReader (compatible with ES5/ES2015 targets) */
  private readBlobAsText(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob);
    });
  }
}
